import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { Creators as periodActions } from '../../../domain/useCases/getCurrentPeriodUseCase';
import { Creators as scheduleActions } from '../../../domain/useCases/schedule/getScheduleOnlyUseCase';
import { Creators as filterActions } from '../../../domain/useCases/schedule/filterByWeekUseCase';
import { Creators as activitiesActions } from '../../../domain/useCases/schedule/getActivitiesUseCase';
import { Creators as examsActions } from '../../../domain/useCases/schedule/getExamsUseCase';
import { Creators as selectAcademicDateActions } from '../../../domain/useCases/selectAcademicDateUseCase';
import { Creators as reinforcementsActions } from '../../../domain/useCases/schedule/getReinforcementUseCase';
import DateHelpers from '../../../core/helpers/DateHelper';
import XCheckpointManager from '../../../core/managers/xcheckpointManager';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';
import { useNewStudents } from '@/core/hooks/useNewStudents';

const useGetSchedule = () => {
  const [loadStart, setLoadStart] = useState(false);
  const dispatch = useDispatch();
  const [slideDaySelected, setSlideDaySelected]: any = useState(null);
  // selectors
  const { data } = useSelector((state: any) => state.profile);
  const { data: dataAcademicdates, isLoading: isLoadingAcademicDates } =
    useSelector((state: any) => state.academicdates);
  const { period: dataPeriod, isLoading: isLoadingPeriod } = useSelector(
    (state: any) => state.currentPeriod
  );
  const { data: getDataPeriods } = useSelector(
    (state: any) => state.getPeriods
  );

  const { data: dataSchedule, isLoading: isLoadingSchedule } = useSelector(
    (state: any) => state.scheduleOnly
  );
  const { data: dataFilter } = useSelector((state: any) => state.filterByWeek);
  const { data: dataProfessors } = useSelector((state: any) => state.professor);
  const { data: dataActivities, isLoading: isLoadingActivities } = useSelector(
    (state: any) => state.activities
  );
  const { data: dataReinforcements, isLoading: isLoadingReinforcements } =
    useSelector((state: any) => state.reinforcements);
  const { data: dataExams } = useSelector((state: any) => state.exams);
  const { data: dataExternalSettings } = useSelector(
    (state: any) => state.externalSettings
  );

  function currentPeriod() {
    if (getDataPeriods.length === 2) {
      return getDataPeriods.find((period: any) => period.isCurrent === true);
    }
    return getDataPeriods[0];
  }

  function directToDate() {
    if (currentPeriod && dataExternalSettings?.dates_by_periods) {
      const date = JSON.parse(dataExternalSettings.dates_by_periods).find(
        (period: any) => period.period === currentPeriod().period
      );
      if (date) {
        return date.startDate;
      }
      return null;
    }
    return null;
  }

  const availableDays = [1, 2, 3, 4, 5, 6, 7];
  const firstDateOfWeek = DateHelpers.getFirstDayOfWeekFromTimestamp(
    new Date().getTime()
  );
  const initialWeek = availableDays.map((numberDay: number) =>
    DateHelpers.addDays(new Date(firstDateOfWeek), numberDay - 1)
  );
  const [dataWeek, setDataWeek] = useState({
    week: initialWeek,
    selectedDate: new Date()
  });

  const modificarDiaSeleccionado = (date: Date) => {
    const newFirstDayOfWeek = DateHelpers.getFirstDayOfWeekFromTimestamp(
      date.getTime()
    );
    const newWeek = availableDays.map((numberDay: number) =>
      DateHelpers.addDays(new Date(newFirstDayOfWeek), numberDay - 1)
    );
    setDataWeek({
      week: newWeek,
      selectedDate: date
    });
    dispatch(scheduleActions.getScheduleOnly(newWeek[0].getTime(), null));
  };

  const newStudent = !useNewStudents();

  const extractDate = () => {
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);
    const fechaParam = params.get('fecha');
    return fechaParam;
  };

  const compareDate = (date: any) => {
    if (date > new Date()) {
      return date;
    }
    return null;
  };

  const dateForNew = () =>
    compareDate(new Date(`${directToDate()}T00:00:00.000`));

  useEffect(() => {
    document.title = `UTP + Portal | Horarios`;
    XCheckpointManager.registerStart('Calendario');
    dispatch(periodActions.getCurrentPeriod());
    dispatch(activitiesActions.getActivities());
    dispatch(reinforcementsActions.getReinforcement());

    setLoadStart(true);
    GoogleAnalytics.writeLog('Menu-Horario', 'Menu');
    return () => {
      document.title = `UTP+ Portal | Universidad Tecnológica del Perú`;
    };
  }, []);

  useEffect(() => {
    if (data && dataExternalSettings) {
      const { portal_enable_exams: enabledExams } = dataExternalSettings;
      if (enabledExams === 'true')
        dispatch(examsActions.getExams(currentPeriod().period));
    }
  }, [data, dataExternalSettings]);

  useEffect(() => {
    if (getDataPeriods) {
      const selectedDate = extractDate()
        ? new Date(`${extractDate()}T00:00:00.000`)
        : dateForNew();
      if (selectedDate) modificarDiaSeleccionado(selectedDate);
      else
        dispatch(
          scheduleActions.getScheduleOnly(firstDateOfWeek.getTime(), null)
        );
    }
  }, [getDataPeriods, newStudent]);

  useEffect(() => {
    if (
      dataSchedule &&
      !isLoadingSchedule &&
      dataPeriod &&
      !isLoadingPeriod &&
      dataActivities &&
      !isLoadingActivities &&
      dataReinforcements &&
      !isLoadingReinforcements &&
      dataAcademicdates &&
      !isLoadingAcademicDates &&
      loadStart
    ) {
      XCheckpointManager.registerEnd('Calendario');
      setLoadStart(false);
    }
  }, [
    dataProfessors,
    dataSchedule,
    dataPeriod,
    dataActivities,
    dataReinforcements,
    dataExams,
    dataAcademicdates,
    dataExternalSettings
  ]);

  useEffect(() => {
    dispatch(filterActions.filterByWeek(dataWeek));
  }, [
    dataAcademicdates,
    dataWeek.selectedDate,
    dataSchedule,
    dataProfessors,
    dataActivities,
    dataReinforcements,
    dataExams
  ]);

  const handlerSelectDate = (date: Date) => {
    modificarDiaSeleccionado(date);
  };

  const handlerNext = (index: number) => {
    if (index === 0) modificarDiaSeleccionado(new Date());
    else {
      const date: Date =
        index === 1 ? new Date(dataWeek.week[6]) : new Date(dataWeek.week[0]);
      const newDate = new Date(date.setDate(date.getDate() + index));
      modificarDiaSeleccionado(newDate);
    }
  };

  const handlerSelectMobileSlide = (date: Date) => {
    if (isMobile) {
      if (date === null) {
        setSlideDaySelected(null);
      } else {
        setSlideDaySelected(date.getTime());
      }
    }
  };

  const slideChangeHandler = (swiper: any) => {
    const { previousIndex, activeIndex } = swiper;
    if (previousIndex === 2 && activeIndex === 1) return;
    if (previousIndex === 0 && activeIndex === 1) return;
    if (previousIndex === 1 && activeIndex === 0) {
      handlerNext(-1);
    } else if (previousIndex === 1 && activeIndex === 2) {
      handlerNext(1);
    }
  };

  const handleSelectAcademicCalendar = (academicCalendar: any) => {
    dispatch(selectAcademicDateActions.selectAcademicDate(academicCalendar));
  };
  return {
    periodName: dataPeriod?.description,
    weeksByPeriod: dataPeriod?.weeks,
    periodId: dataPeriod?.id,
    selectedDate: dataWeek.selectedDate,
    dates: dataWeek.week,
    today: new Date(),
    isCGT: data?.academic?.isCGT,
    academicdates: dataFilter?.academicCalendarByWeek,
    meetings: dataFilter?.scheduleByWeek,
    virtualMeetings: dataFilter?.virtualMeetingsByWeek,
    currentModule: dataFilter?.currentModule,
    currentWeek: dataFilter?.currentWeek,
    slideDaySelected,
    isClassFlag: dataSchedule?.isClassFlag,
    isLoadingSchedule,
    handlerSelectMobileSlide,
    handlerNext,
    handlerSelectDate,
    slideChangeHandler,
    handleSelectAcademicCalendar
  };
};

export default useGetSchedule;
